// pages/hauler/EarningsScreen.js
// Hauler earnings — read-only summary of completed work.
//
// Reads from the lifted completed slice. Aggregations live in
// utils/haulerEarnings.js. Persisted driver_pay_amt (the payout
// snapshot column) is preferred over a recomputed split for any
// completed job.
//
// Bucket A+B contract: estimate framing, no snapshot-lock copy, no
// gamification, no leaderboards, no points/ranks. Reliability score
// belongs on Profile, not here.

import React, { useMemo } from "react";
import {
  summarizeWeek,
  summarizeByDay,
  jobPayout,
  jobTierLabel,
  jobDurationMin,
} from "../../utils/haulerEarnings";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
};

function fmt$(n) { return "$" + Number(n || 0).toFixed(2); }
function dateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function EarningsScreen({ session, completed = [] }) {
  const weekly = useMemo(() => summarizeWeek(completed), [completed]);
  const days = useMemo(() => summarizeByDay(completed), [completed]);
  const maxDay = Math.max(1, ...days.map(d => d.total));

  // Recent history — completed jobs sorted by completed_at desc.
  const history = useMemo(() => {
    return [...completed]
      .filter(j => j.completed_at)
      .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
      .slice(0, 30);
  }, [completed]);

  return (
    <div style={{ padding: "20px 16px 96px", color: COLORS.text }}>
      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.muted, marginBottom: 4 }}>
          EARNINGS
        </div>
        <div style={{ fontSize: 22, fontWeight: 600 }}>This week</div>
      </div>

      {/* Summary card */}
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "18px 18px 16px",
        marginBottom: 16,
      }}>
        <div style={{ fontSize: 36, fontWeight: 800, color: COLORS.green, letterSpacing: -1, lineHeight: 1 }}>
          {fmt$(weekly.total)}
        </div>
        <div style={{ fontSize: 12, color: COLORS.sub, marginTop: 8 }}>
          {weekly.count} completed · {weekly.avgHourly > 0 ? `~$${weekly.avgHourly.toFixed(0)}/hr avg` : "Hourly estimate pending"}
        </div>
      </div>

      {/* Day bars */}
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "14px 14px",
        marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.sub, textTransform: "uppercase", marginBottom: 12 }}>
          By day · last 7
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 8,
          alignItems: "end",
          height: 110,
        }}>
          {days.map((d, i) => {
            const ratio = maxDay > 0 ? d.total / maxDay : 0;
            const height = Math.max(2, Math.round(ratio * 80));
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                <div style={{
                  fontSize: 10,
                  color: d.total > 0 ? COLORS.text : COLORS.muted,
                  fontWeight: d.total > 0 ? 700 : 500,
                  marginBottom: 4,
                }}>
                  {d.total > 0 ? "$" + Math.round(d.total) : "—"}
                </div>
                <div
                  style={{
                    width: "70%",
                    height,
                    background: d.total > 0 ? COLORS.green : "var(--hl-border)",
                    borderRadius: 6,
                  }}
                  title={`${d.label}: ${fmt$(d.total)} · ${d.count} job${d.count === 1 ? "" : "s"}`}
                />
                <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 6, fontWeight: 600 }}>{d.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {!session && (
        <EmptyState title="Sign in required" body="Sign in to see your earnings history." />
      )}

      {session && history.length === 0 && (
        <EmptyState
          title="No completed jobs yet"
          body="Earnings appear here as you complete jobs."
        />
      )}

      {/* History list */}
      {session && history.length > 0 && (
        <div>
          <SectionHeader label={`History · ${history.length}`} />
          {history.map(job => (
            <HistoryRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ job }) {
  const pay = jobPayout(job);
  const tier = jobTierLabel(job);
  const minutes = jobDurationMin(job);
  const hourly = minutes > 0 ? Math.round(pay / (minutes / 60)) : 0;

  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: "12px 14px",
      marginBottom: 8,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ fontSize: 11, color: COLORS.muted, letterSpacing: 1, textTransform: "uppercase" }}>
          {dateShort(job.completed_at)}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted }}>Zone {job.zone || "—"}</div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0, flex: 1, paddingRight: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {job.addr || "Address not recorded"}
          </div>
          <div style={{ fontSize: 11, color: COLORS.sub, marginTop: 2 }}>
            {tier}{minutes > 0 ? ` · ~${minutes} min` : ""}{hourly > 0 ? ` · ~$${hourly}/hr` : ""}
          </div>
        </div>
        <span style={{ fontSize: 16, fontWeight: 800, color: COLORS.green }}>
          {fmt$(pay)}
        </span>
      </div>
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
      color: COLORS.muted, textTransform: "uppercase",
      margin: "8px 2px 8px",
    }}>
      {label}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "20px 18px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}
